import { useCallback, useEffect, useMemo, useRef } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingSpotBidOrigin,
  BiddingSpotOrderMethod,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldErrors } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Box,
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldLabel as EcFieldLabel,
  FieldRoot,
  HStack,
  Input,
  Link,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  TertiaryButton,
  Text,
  VStack,
  chakra,
} from '@enechain/ecloud-designsystem'

import { BidLimitHint } from '~/components/bidding/common/BidLimitHint'
import {
  IssuerSelectorView,
  useIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import { areaLabelOf } from '~/components/bidding/common/areaLabel'
import {
  AREA_UNSELECTED,
  isAreaSelectableForIssuer,
  selectedAreaOf,
  settledIssuerOption,
} from '~/components/bidding/common/bidIssuer'
import { initialBidArea } from '~/components/bidding/common/lastBidArea'
import type { DsSelectOption } from '~/components/common/DsSelect'
import { FormSelect } from '~/components/common/FormSelect'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { RequiredMark } from '~/components/common/RequiredMark'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useCompanyInfo } from '~/hooks/useCompanyInfo'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import {
  isMarketOrderAllowed,
  perBidVolumeLimitExceeded,
  resolveAreaSideLimit,
} from '~/models/bidLimits'
import { formatDateDisplay } from '~/models/dateTime'
import {
  JEPX_VOLUME_CONSTRAINT,
  wrapNumberInputChange,
} from '~/models/numberInputConstraint'
import type { BidFormField } from '~/pages/SpotBidding/components/BidFormAlerts'
import { BidFormAlerts } from '~/pages/SpotBidding/components/BidFormAlerts'
import { FeeAutoConsiderHint } from '~/pages/SpotBidding/components/FeeAutoConsiderHint'
import { MemoFields } from '~/pages/SpotBidding/components/MemoFields'
import { useSpotBidIssuerForm } from '~/pages/SpotBidding/hooks/useSpotBidIssuerForm'
import { useSpotFeeAutoConsider } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsider'
import { useSpotFeePriceEntries } from '~/pages/SpotBidding/hooks/useSpotFeePriceEntries'
import { detectBidFormAlerts } from '~/pages/SpotBidding/models/bidFormAlerts'
import {
  buildJepxNote,
  jepxFreeTextMaxLength,
  parseEmissionComment,
  stripEmissionComment,
} from '~/pages/SpotBidding/models/emissionLinking'
import {
  spotOrderPriceForChecks,
  spotPriceEntryText,
} from '~/pages/SpotBidding/models/feePreview'
import { SPOT_KOMA_OPTIONS } from '~/pages/SpotBidding/models/komaOptions'
import { shouldShowNonDraftBidNotice } from '~/pages/SpotBidding/models/nonDraftBidNotice'
import type { IndividualBidFormInput } from '~/pages/SpotBidding/models/schema'
import {
  desiredPriceIndividualBidFormSchema,
  individualBidFormSchema,
} from '~/pages/SpotBidding/models/schema'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import type { InversionInput } from '~/pages/SpotBidding/models/spotBidValidation'
import {
  type SpotBidFeeAutoConsiderEntry,
  appliesPerBidVolumeLimit,
  bidRowToForm,
  editedBidOrigin,
} from '~/pages/SpotBidding/models/spotBidWrite'
import { priceInputConstraint } from '~/pages/SpotBidding/models/spotFeeAutoConsider'
import { selectableAreaCodes } from '~/pages/SpotBidding/models/tradableArea'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types の既知不具合で `FieldLabelProps` / `FieldErrorTextProps`
// (= `extends HTMLChakraProps<...>, UnstyledProp`) が `{ unstyled }` のみに collapse し、
// children を含む全 props を JSX で受け取れなくなる。 ecloud 由来の component をそのまま使い、
// 取りこぼした props (children / style) を補う BoxProps で型付けし直す。
// `htmlFor` は label ↔ input の明示的な関連付けに使う (= chakra v3 Field の
// auto id 共有が ecloud Input まで伝播せず getByRole の accessible name が
// 切れるため、 input 側 `id` と一致させる)。
const FieldLabel = EcFieldLabel as React.FC<BoxProps & { htmlFor?: string }>
const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>
// 同じ chakra v3.29 型解決の collapse (= 上記 FieldLabel と同じ既知不具合) が
// `RadioGroupItem` / `RadioGroupItemText` の `children` にも及ぶため同様に cast する。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  BoxProps & { value: string; disabled?: boolean }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<BoxProps>
// フォーム側の `orderMethod` は zod schema 由来の生 number (= `direction`/`areaCode`
// 等の他 enum field と同じ規約)。 enum member と直接比較すると
// @typescript-eslint/no-unsafe-enum-comparison に触れるため number 化する
// (`Supply48Data/models/supply48QueryParams.ts` と同じ回避)。
const ORDER_METHOD_MARKET: number = BiddingSpotOrderMethod.MARKET

/**
 * モーダルの開き方。 新規作成 / 編集 / 複製 / 再入札は排他なので 1 つの判別共用体で表す
 * (= 「編集対象と複製元が両方ある」 のような組み合わせを型で作れなくする)。
 *
 * - `create`: 新規個別作成 (既定値で開く)
 * - `edit`: 既存札の編集 (`bid` の値で開き、 保存はその札を置き換える)
 * - `duplicate`: 失効した札の「複製」(AC-BSP-011)。 id・承認状態は引き継がず、
 *   エリア/売買/コマ/価格/数量/メモだけを新規作成フォームの初期値として流し込む
 *   (= 保存すると元の札とは別の新しい BidId が作られ、 元の札は変わらない)。
 *   `targetDate` は保存先の受渡日 (= 親 page が締切前の受渡日へ切り替えた値)。
 *   フォームは受渡日を持たないため、 案内 Alert で明示する
 * - `rebid`: 取消済みの札からの再入札 (AC-BSP-081)。 新規作成の初期値だけを元の札から
 *   流し込む。 編集と違い**編集モードにはしない**: 再入札は新規 BidId の新しい札で、
 *   取消した札には触れないため (取消札は監査履歴として残す)
 */
export type IndividualBidModalSource =
  | { mode: 'create' }
  | { mode: 'edit'; bid: SpotBidRow }
  | { mode: 'duplicate'; source: SpotBidRow; targetDate: Date }
  | { mode: 'rebid'; source: SpotBidRow }

const CREATE_SOURCE: IndividualBidModalSource = { mode: 'create' }

/**
 * 必須フィールドの赤い `*` (= design `.form-label.required` の擬似要素相当)。
 * `FieldRequiredIndicator` は上記 Field type collapse の影響で機能しないため自前で描画する
 * (IntradayBidCreateModal / CreateWorkflowCasesDialog と同パターン)。
 * 表示専用モード (`readOnly`) では必須マークを外す (= 8080 の readOnly 挙動に準拠)。
 */
type Props = {
  isOpen: boolean
  /**
   * ページの受渡日。 発注主体区分の選択肢とエリアの選択可否をこの日の広域マスタで求める
   * (保存時の受渡契約コード解決と同じ断面。 #8842)。
   */
  deliveryDate: Date
  /** 提出中 (= 親の Create/Update mutation が pending)。 */
  isSubmitting: boolean
  /** サーバーエラー文言 (= 親が ConnectError から訳した文字列)。 */
  errorMessage: string | undefined
  /**
   * 何を開くか ([[IndividualBidModalSource]])。 省略時は新規作成。
   */
  source?: IndividualBidModalSource
  /**
   * 入札締切前か (= 親 page の市場フェーズ判定 `pre` / `open`)。
   *
   * 「下書きへ戻す手順」 の案内 (AC-BSP-066) を出せるかの判定に使う。 承認軸
   * (`initialBid.approvalState`) は締切を跨いでも変わらないため、 この軸が無いと
   * 約定済・失効の札にまで「取下 (入札締切前のみ) で下書きに戻して」 と案内してしまう
   * ([[shouldShowNonDraftBidNotice]])。 呼び出し側が状態を渡し忘れて案内が黙って
   * 消える / 出続けるのを防ぐため必須 prop にしている。
   */
  isBeforeDeadline: boolean
  /**
   * ワークフロー経由で画面が read-only か (= `?workflowId=` の完了済ケース、
   * およびケース状態を取得できていない間の fail-closed)。
   *
   * `isBeforeDeadline` と同じく「下書きへ戻す手順」 の案内 (AC-BSP-066) を出せるか
   * の判定に使う。 確定済ワークフロー経由の入札は 申請取消 / 再編集 / 取下 を
   * backend が拒否し (AC-BSP-058)、 画面も導線を出さないため
   * ([[shouldShowNonDraftBidNotice]])。 `readOnly` と分けているのは、 `readOnly` が
   * 締切・承認状態も畳み込んだ「入力させない」 の合成条件で、 案内条件
   * (= 下書きへ戻せるか) と母集合が違うため。
   */
  isWorkflowReadonly: boolean
  /**
   * 表示専用 (#5356 Des 裁定 2026-08-18「全行開く・**下書き以外は表示専用**」)。
   * 一覧の行クリックは下書き以外の札でも開くが、 提出済・承認済の札は編集できない。
   * 入力欄を disabled にし、 footer から保存を外して「閉じる」だけにする。
   *
   * [design 差異] 8080 の `readOnly` は required 属性と必須マークを外すだけで入力自体は
   * 操作できるが、 実装は Des 裁定に従って**編集させない**。 保存できない札の入力欄が
   * 触れると「直せそうに見えて保存できない」 ため。
   */
  readOnly?: boolean
  /**
   * 取引不可エリア (`ListTradableAreas` の取引可能エリアに無いエリア)。 選択肢から除く
   * (AC-BSP-005b。 発注できないエリアの札を作らせない)。 取引可能エリアを取得できて
   * いない場合は空集合 = 制限しない (page の fallback と同じ扱い)。 編集中の札が
   * 取引不可エリアのものだった場合は、 その値だけは選択肢に残す (= 開いた瞬間に
   * エリアが変わるのを防ぐ)。
   */
  disabledAreas?: ReadonlySet<AreaCode>
  /** ページのエリアフィルタで単一エリアを指定している場合、 その AreaCode (AC-BSP-123)。 */
  defaultAreaCode?: AreaCode
  /**
   * 業務エリア設定で許可された JEPX 対象エリア (AC-BSP-019 / `~/hooks/useBusinessAreaFilter`)。
   * これ以外のエリアは選択肢に残して選べなくし「業務エリア対象外」 を添える (AC-BSP-005b)。
   */
  areaCodes: readonly AreaCode[]
  /**
   * 同じ受渡日の他の札 (= 売買逆転の判定相手)。 編集中の札自身が含まれていてよい
   * (id で除外する)。 渡さない場合は逆転判定を行わない。
   */
  siblingBids?: readonly InversionInput[]
  /**
   * 新規作成で開いた時の発注主体区分 (= 画面上部ツールバーで選択中の区分。 AC-BSP-005d)。
   * 未指定・選択肢に無い時は [[IssuerSelector]] が優先順の最上位を既定選択する。
   * 再入札・複製・編集は元の札の区分を使い、 この値で差し替えない。
   */
  defaultIssuerType?: LicenseType
  onClose: () => void
  /** 札 1 件を確定する。 親が当日 request への Create/Update に変換する。 */
  onSubmit: (bid: IndividualBidSubmission) => void
}

/**
 * 確定した札 1 件。 起票由来は事前チェックに使った値で、 親はそのまま送る (#8165)。 `feeAutoConsider` が
 * 付いた札の価格は希望価格で、 入札価格は backend が算出する (AC-BSP-004b)。 付かない札の価格は入札価格。
 */
export type IndividualBidSubmission = IndividualBidFormInput &
  SpotBidFeeAutoConsiderEntry & {
    origin: BiddingSpotBidOrigin
  }

/** エリアを入れる form の field。 `useSelectableAreas` へ渡す (module 定数で参照を固定)。 */
const AREA_FIELD_NAMES = ['areaCode'] as const

/**
 * 新規作成時の初期値。 売/買・コマは閉じた必須集合なので空にせず先頭相当を
 * 選んだ状態で開く (コマは 1 = 00:00〜00:30)。 エリアは誤選択の事故が大きいので
 * 空のまま `common:form.selectPlaceholder` (=「選択」) を出す。
 *
 * 発注主体区分は画面上部ツールバーで選択中の区分 (`defaultIssuerType`) で開く。 それが
 * 無い・選択肢に無い時は、 選択肢が届いた時点で優先順の最上位を既定選択する
 * ([[IssuerSelector]] の `selectsDefault`、 AC-BSP-005d)。 選択肢が無い間は
 * UNSPECIFIED のままにして submit を通さない。
 */
const NEW_INDIVIDUAL_BID_DEFAULTS = {
  direction: Side.BUY,
  issuerType: LicenseType.UNSPECIFIED,
  timeCode: 1,
  // 価格種別 (指値 / 成行、 AC-BSP-004a)。 指値を既定にする (= 従来どおりの挙動)。
  orderMethod: BiddingSpotOrderMethod.LIMIT,
  // メモ・排出係数紐付けも明示的に空へ戻す。 モーダルは保存後もマウントされたままで、
  // 値を省くと reset しても直前に開いた札の入力欄の値が残る。
  memoJepx: '',
  memoFree: '',
  emissionLinked: false,
  emissionAlpha: '',
} as const

/**
 * 個別作成モーダル (design `spot.html` `#bid-new-modal`)。
 * 1 コマ × 1 エリア × 1 方向の札 1 件を入力する。 受渡日は親 page の選択値を
 * 使うためフォームには持たない (= 当日 request に追加 / 新規作成は親が判断)。
 */
export const IndividualBidModal = ({
  isOpen,
  deliveryDate,
  isSubmitting,
  errorMessage,
  source = CREATE_SOURCE,
  isBeforeDeadline,
  isWorkflowReadonly,
  readOnly = false,
  disabledAreas,
  areaCodes,
  defaultAreaCode,
  siblingBids,
  defaultIssuerType = LicenseType.UNSPECIFIED,
  onClose,
  onSubmit,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 開いた時点の値だけを使う (deps に入れると、 開いている間に値が変わった時に入力中の
  // form を初期値へ戻してしまう)。
  const defaultIssuerTypeRef = useRef(defaultIssuerType)
  defaultIssuerTypeRef.current = defaultIssuerType
  const initialBid = source.mode === 'edit' ? source.bid : undefined
  const duplicateFrom = source.mode === 'duplicate' ? source.source : undefined
  const rebidFrom = source.mode === 'rebid' ? source.source : undefined
  const isEdit = initialBid !== undefined
  // AC-BSP-001a ※: 下書きの編集ではエリアを変えない (変える時は札を削除して作り直す)。
  // 保存済みの区分が選択肢に無い時は区分だけ選び直させる (AC-BSP-135)。
  const savedIssuerType = useMemo(
    () =>
      initialBid === undefined
        ? undefined
        : bidRowToForm(initialBid).issuerType,
    [initialBid],
  )
  const isAreaLocked = readOnly || isEdit
  /**
   * AC-BSP-066 の契機の札を開いている (= 承認申請中 / 承認済 / 差戻し / 入札中)。
   * 判定は「下書き以外」 ではなく、 下書きへ戻せる状態かで決める
   * ([[shouldShowNonDraftBidNotice]] に理由と各状態の写像)。
   */
  const showsNonDraftNotice = shouldShowNonDraftBidNotice({
    bid: initialBid,
    isBeforeDeadline,
    isWorkflowReadonly,
  })
  const { feeAutoConsider, isSettingsPending } = useSpotFeeAutoConsider(isOpen)
  const {
    control,
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IndividualBidFormInput>({
    resolver: zodResolver(
      feeAutoConsider.kind === 'on'
        ? desiredPriceIndividualBidFormSchema
        : individualBidFormSchema,
    ),
    defaultValues: NEW_INDIVIDUAL_BID_DEFAULTS,
  })

  // 成行→指値の自動フォールバック (下記 effect) が適用済みかを覚える ref。
  // 編集中の bid が変わった時だけ (初期表示に戻す)、bidRowToForm の orderMethod
  // 再適用で揉み消されない。
  const marketFallbackAppliedRef = useRef(false)
  useEffect(() => {
    marketFallbackAppliedRef.current = false
  }, [initialBid?.id])

  // 編集で開いた札を form へ戻し済みか (下記 [編集] effect)。 開き直す・別の札を開くたびに戻し直す。
  const formRestoredRef = useRef(false)
  useEffect(() => {
    formRestoredRef.current = false
  }, [isOpen, initialBid?.id])

  // ----- 入札制限インラインヒント (design (B))。SPOT_NORMAL の実設定値を照合する -----
  const {
    limits: bidLimits,
    marketOrderPolicyEntries,
    isLoading: bidLimitsLoading,
  } = useBidLimits(BidSegment.SPOT_NORMAL)
  const watchedAreaCode = selectedAreaOf(watch('areaCode'))
  const watchedDirection = watch('direction')
  const watchedPrice = watch('priceYenPerKwh')
  const watchedVolume = watch('volumeMw')
  const watchedOrderMethod = watch('orderMethod')
  const isMarket = watchedOrderMethod === ORDER_METHOD_MARKET
  // 成行注文許可 (AC-BSP-004a、 SoT は `bid-limits.md` §1-1)。 エリア未選択 / 取得中・
  // 取得失敗はいずれも false (= fail-safe、 [[isMarketOrderAllowed]] 参照)。
  const marketAllowed =
    watchedAreaCode !== undefined &&
    isMarketOrderAllowed(
      marketOrderPolicyEntries,
      BidSegment.SPOT_NORMAL,
      watchedAreaCode,
    )
  // 成行不許可の表示用フラグ (RadioGroupItem の disabled / ヒント文言)。 エリア未選択
  // はそのまま不許可扱い (従来通り)。 エリア選択済みで bidLimitsLoading 中は
  // marketAllowed が fail-safe で false になり得るが、 それは「不許可」ではなく
  // 「未確定」なので、 下のフォールバック effect と同じく取得完了を待ってから
  // 「不許可」を確定する (devin-review 指摘。待たないと matrix 到着までの一瞬、
  // 成行可エリアでも disabled + 禁止ヒントが点滅する)。
  const marketBlocked =
    watchedAreaCode === undefined || (!bidLimitsLoading && !marketAllowed)
  // エリア + 売/買 が確定した時点で制限を引く (どちらか未確定なら undefined = 非表示)。
  const resolvedLimit =
    watchedAreaCode !== undefined && watchedDirection !== undefined
      ? resolveAreaSideLimit(bidLimits, watchedAreaCode, watchedDirection)
      : undefined
  // 成行は価格を持たない (AC-BSP-050) ので、 価格の入札制限判定・ヒントからは除く。
  const hintPrice =
    !isMarket && Number.isFinite(watchedPrice) ? watchedPrice : undefined
  const hintVolume = Number.isFinite(watchedVolume) ? watchedVolume : undefined
  // 上限数量(1札) の hint・保存時の事前チェック・送信値の起票由来はここで決める (#8165)。
  const originFor = (volumeMw: number): BiddingSpotBidOrigin =>
    initialBid !== undefined
      ? editedBidOrigin(initialBid.origin, initialBid.volumeMwh, volumeMw)
      : BiddingSpotBidOrigin.MANUAL

  // 成行選択時は価格入力欄を無効化する (AC-BSP-004a)。 disabled 化しても RHF の
  // register 値は残りうるため、 表示中は強制的に 0 に揃えておく
  // (実送信時の最終防衛は `formToBidInput` 側)。 編集対象が元々成行の札だった場合の
  // 初期表示 (price="0") にもここで揃う。
  useEffect(() => {
    if (isMarket) {
      setValue('priceYenPerKwh', 0, { shouldValidate: true })
    }
  }, [isMarket, setValue])

  // 成行選択中にエリアを変更して成行注文許可が無くなった場合、 指値へ自動フォールバック
  // する (design `spot.html` `_updateBnewPriceType` の「成行選択中に不可へ→指値へ
  // fallback」と同じ挙動、 codex-review 指摘)。 取得中 (`bidLimitsLoading`) の間は
  // 判定不能であって「不許可」ではないため、 取得完了を待ってから評価する — 待たずに
  // 評価すると、 成行札を編集で開いた直後 (matrix 未着で marketAllowed が一時的に
  // false) に誤って指値へ落としてしまう。
  //
  // フォールバック済みかは ref で覚える (= 編集復元 effect が退避先)。 編集対象の
  // 会社情報が遅れて届くと下の [編集] effect が再実行され、
  // 都度 `bidRowToForm(initialBid)` で orderMethod を元の成行へ戻してしまい、 この
  // フォールバックが揉み消される (この PR で実際に発生させて確認したレース)。
  useEffect(() => {
    if (!bidLimitsLoading && isMarket && !marketAllowed) {
      marketFallbackAppliedRef.current = true
      setValue('orderMethod', BiddingSpotOrderMethod.LIMIT, {
        shouldValidate: true,
      })
    }
  }, [bidLimitsLoading, isMarket, marketAllowed, setValue])

  // ----- 発注主体区分 -----
  // 選んだ区分をそのまま `issuerType` (= 書込の issuer_type) として送る。
  const issuerOptions = useIssuerOptions(deliveryDate)
  const watchedIssuerType = watch('issuerType') as LicenseType
  const keepAreaCode = initialBid?.areaCode
  const keepAreaCodes = useMemo(
    () => (keepAreaCode !== undefined ? [keepAreaCode] : []),
    [keepAreaCode],
  )
  const issuerForm = useSpotBidIssuerForm({
    deliveryDate,
    active: isOpen,
    mode: source.mode,
    readOnly,
    savedIssuer: savedIssuerType,
    issuerField: 'issuerType',
    issuerValue: watchedIssuerType,
    areaFields: AREA_FIELD_NAMES,
    areas: [watchedAreaCode],
    candidateAreaCodes: areaCodes,
    disabledAreas,
    keepAreaCodes,
    unselectedValue: AREA_UNSELECTED,
    getValues,
    setValue,
    setError,
    handleSubmit,
    reset,
  })
  const {
    areaSelectOptionsFor,
    selected: selectedIssuer,
    reselectBlock,
    resetForm,
  } = issuerForm
  const isSavedIssuerFixed = issuerForm.fixed

  // 会社情報 (登録小売電気事業者 ID)。 排出係数紐付けの判別コメント組み立てに使う。
  const companyInfo = useCompanyInfo()

  // open 時に form を初期化する。 Chakra Modal は閉じても content を unmount せず form
  // state が残るため、 submit 成功で親が open=false にしただけでは reset されず、 次回
  // open 時に前回値が残って二重送信を誘発する。 これを防ぐため毎 open で reset する。
  //
  // 新規作成と編集で effect を分けている。 編集側は遅延到着する master (会社情報) が
  // 届くたびに復元をやり直す必要がある一方、 新規作成側で同じ deps を持つと入力途中に
  // master が届いた瞬間に入力値が既定値へ戻ってしまうため。

  const initialAreaBasisRef = useRef({
    areaCodes,
    defaultAreaCode,
    disabledAreas,
    issuerOptions,
  })
  initialAreaBasisRef.current = {
    areaCodes,
    defaultAreaCode,
    disabledAreas,
    issuerOptions,
  }

  // [新規作成 / 再入札] 既定値 (再入札なら取消札の内容) で開く。 deps は open 状態と
  // 再入札元だけ (= master の遅延到着で入力を消さない)。 再入札は取消札の発注主体
  // 区分も引き継ぐ (その受渡日の選択肢に無ければ [[IssuerSelector]] が未選択に戻し、
  // 既定選択をやり直す)。
  useEffect(() => {
    if (!isOpen || (source.mode !== 'create' && source.mode !== 'rebid')) return
    if (rebidFrom !== undefined) {
      resetForm({
        ...NEW_INDIVIDUAL_BID_DEFAULTS,
        ...bidRowToForm(rebidFrom),
      })
      return
    }
    const basis = initialAreaBasisRef.current
    const issuerType = defaultIssuerTypeRef.current
    resetForm({
      ...NEW_INDIVIDUAL_BID_DEFAULTS,
      issuerType,
      areaCode: initialBidArea(
        'spot',
        basis.defaultAreaCode,
        (code) =>
          selectableAreaCodes(
            basis.disabledAreas,
            basis.areaCodes,
            [],
          ).includes(code) &&
          isAreaSelectableForIssuer(
            settledIssuerOption(basis.issuerOptions, issuerType),
            code,
          ),
      ),
    })
  }, [isOpen, source.mode, rebidFrom, resetForm])

  // [複製] (AC-BSP-011) 複製元の内容を新規作成フォームへ流し込む。 `bidRowToForm`
  // は編集時と同じ変換を使い、 発注主体区分も複製元の区分を引き継ぐ (再入札と同じ。
  // 複製先の受渡日の選択肢に無ければ [[IssuerSelector]] が未選択に戻し、 新規作成の
  // 既定選択 (AC-BSP-005d) をやり直す)。 emission 関連 (memoJepx の判別コメント) は
  // 引き継がない — 判別コメントは
  // 「今回のリクエストに実際に載る内容」 の確認を経て初めて有効化すべきもので、
  // 複製直後に無条件で有効化すると排出係数紐付けの誤爆になりうる。 JEPX自由記述欄
  // には [[stripEmissionComment]] で判別コメントを除いた残りだけを戻す (生の memoJepx
  // を戻すと、 紐付け OFF のまま判別コメントが自由記述として JEPX へ送られる)。
  // 会社情報 (retailerId) の取得状況に依らず除くので、 取得失敗中や ID の変更後でも
  // 判別コメントは残らず、 effect も retailerId の到着で再実行されない。
  useEffect(() => {
    if (!isOpen || duplicateFrom === undefined) return
    resetForm({
      ...bidRowToForm(duplicateFrom),
      emissionLinked: false,
      emissionAlpha: '',
      memoJepx: stripEmissionComment(duplicateFrom.memoJepx),
    })
  }, [isOpen, duplicateFrom, resetForm])

  // [編集] 行の値を form に戻す。 保存済み memo_jepx を判別コメントとしてパースし、
  // 排出係数紐付けの checkbox / 英字へ復元する (自社 ID 一致時のみ。 retailerId が
  // open 後に遅延到着した場合も復元し直すため deps に含める)。
  //
  // 発注主体区分は札の区分をそのまま選択値にする ([[bidRowToForm]])。 その区分が
  // 受渡日の選択肢に無い時は [[IssuerSelector]] が未選択に戻し、 読み取り専用 (`isLocked`)
  // の編集でもこの時だけは選び直させる。
  // 発注主体を持たない行 (履歴 projection 由来、 #6094) は UNSPECIFIED のまま
  // (= schema の min(1) が選択を促す)。 札の区分を戻すのは開いた時の 1 度だけで、
  // 会社情報の遅延到着による再実行では form の今の区分を保つ — 戻し直すと、
  // セレクタが未選択に戻した区分 (選択肢に無い) が表示されないまま form に復活して
  // 送信されうる。
  //
  // JEPX自由記述欄 (issue #6154) は判別コメントを除いた残りの自由記述だけを入力欄へ
  // 戻す。 会社情報が届くまで自由記述欄は非活性 (紐付け checkbox も未登録扱いで
  // 非活性) なので、 到着で再実行しても利用者の入力を上書きしない。 そのため保存済み
  // 値から毎回分解し直してよく、 編集済みかを覚えておく必要が無い。
  useEffect(() => {
    if (!isOpen || initialBid === undefined) return
    const emission = parseEmissionComment(
      initialBid.memoJepx,
      companyInfo.retailerId,
    )
    // 再実行 (会社情報の後着など) では、 会社情報に依存する項目だけを更新する。 form 全体を戻すと、 先に届いた
    // 逆算で希望価格へ直した価格欄や、 利用者が切り替えた価格種別の一部だけが保存済みの値に戻り、 組み合わせが
    // 食い違ったまま送られる (例: 成行に切り替えた価格 0 のまま指値に戻る)。 3 項目を setValue で入れ替えるだけの
    // 形は採らない: 逆算を待つ間に無効だった価格欄を後から直した時、 プレビューの問い合わせが止まった (test で観測)。
    if (formRestoredRef.current) {
      resetForm(
        {
          ...getValues(),
          emissionLinked: emission.linked,
          emissionAlpha: emission.alpha,
          memoJepx: emission.freeText,
        },
        'refresh',
      )
      return
    }
    formRestoredRef.current = true
    const restoredForm = bidRowToForm(initialBid)
    resetForm({
      ...restoredForm,
      // 成行→指値の自動フォールバックが適用済みなら元の成行へ戻さない (上の ref)。
      orderMethod: marketFallbackAppliedRef.current
        ? BiddingSpotOrderMethod.LIMIT
        : restoredForm.orderMethod,
      emissionLinked: emission.linked,
      emissionAlpha: emission.alpha,
      memoJepx: emission.freeText,
    })
  }, [isOpen, initialBid, resetForm, getValues, companyInfo.retailerId])

  const openedBid = rebidFrom ?? duplicateFrom ?? initialBid
  const priceEntryText = spotPriceEntryText(hintPrice)
  const readPrice = useCallback(() => getValues('priceYenPerKwh'), [getValues])
  const writePrice = useCallback(
    (_key: 'price', price: number) => {
      setValue('priceYenPerKwh', price)
    },
    [setValue],
  )
  const priceEntries = useSpotFeePriceEntries({
    isOpen,
    fee: feeAutoConsider,
    isSettingsPending,
    restoreKey: source.mode,
    pendingPresentation: 'keepPriceLabel',
    entries: [
      {
        key: 'price',
        seed:
          openedBid !== undefined &&
          openedBid.orderMethod !== BiddingSpotOrderMethod.MARKET
            ? {
                direction: openedBid.direction,
                orderPriceYenPerKwh: openedBid.priceYenPerKwh,
              }
            : undefined,
        input:
          priceEntryText !== undefined &&
          watchedAreaCode !== undefined &&
          watchedDirection !== undefined
            ? {
                areaCode: watchedAreaCode,
                direction: watchedDirection,
                desiredPriceYenPerKwh: priceEntryText,
              }
            : undefined,
        isMarket,
      },
    ],
    readPrice,
    writePrice,
  })
  const priceFee = priceEntries.fee
  const feePreview =
    priceEntries.previews.price ??
    ({ kind: 'idle', hint: 'incomplete' } as const)
  const orderPriceForAlerts = spotOrderPriceForChecks({
    fee: priceFee,
    preview: feePreview,
    enteredPrice: hintPrice,
  })
  const isSettingsUnavailable = !readOnly && isSettingsPending
  const isFeeAutoConsiderBlocking =
    !readOnly && priceEntries.isSubmissionBlocking
  const priceConstraint = priceInputConstraint(priceFee)
  const isPriceEntryPending = priceEntries.isPriceEntryPending

  // ----- 売買逆転 / JEPX 制度上限の警告 (design `#bnew-row-alert`) -----
  // 入力値から都度判定する (= 一覧の警告アイコンと同じ判定関数を使う)。
  const watchedTimeCode = watch('timeCode')
  // ON の間の入力欄は希望価格なので、 売買逆転・JEPX 制度上限・入札制限は、 手数料単価を加減算した
  // 入札価格で判定する (保存済みの入札価格や兄弟札の入札価格と同じ量で比べる。 AC-BSP-004b)。
  const alertPrice = orderPriceForAlerts
  const formAlerts = detectBidFormAlerts(
    {
      areaCode: watchedAreaCode,
      timeCode: Number.isFinite(watchedTimeCode) ? watchedTimeCode : undefined,
      direction: watchedDirection,
      priceYenPerKwh: alertPrice,
      volumeMw: hintVolume,
      orderMethod: watchedOrderMethod,
    },
    siblingBids ?? [],
    initialBid?.id,
  )

  /** alert の CTA から該当の入力欄へ飛ぶ (design も focus + 全選択する)。 */
  const focusField = (field: BidFormField): void => {
    const id =
      field === 'price'
        ? 'individualBid-priceYenPerKwh'
        : 'individualBid-volumeMw'
    const el = document.getElementById(id)
    if (el instanceof HTMLInputElement) {
      el.focus()
      el.select()
    }
  }

  const handleClose = (): void => {
    resetForm()
    onClose()
  }

  // 紐付け ON なら判別コメントの分だけ自由記述の上限が縮む (spec §7 機能詳細 4)。
  const watchedEmissionLinked = watch('emissionLinked') === true
  const jepxFreeMaxLength = jepxFreeTextMaxLength(
    watchedEmissionLinked && companyInfo.registered
      ? companyInfo.retailerId
      : undefined,
  )

  // AC-3304-1: 価格・数量入力欄は JEPX 範囲外・不正な小数点桁の入力を受け付けない
  // (= constrainNumberInput でラップした onChange。 エラー文言自体は AC-062 準拠で
  // 「0 以上」のまま据え置き、 入力欄側でのみ値域・刻みを制限する)。
  const priceReg = register('priceYenPerKwh', { valueAsNumber: true })
  const volumeReg = register('volumeMw', { valueAsNumber: true })

  // 業務エリアの候補のうち取引可能エリアだけを選択させる。 編集中の札が取引不可
  // エリアだった場合は、 その値が選択肢から消えて別エリアへ化けないよう残す。
  // 区分を選び直せば直る理由は発注主体の欄に出す。 どの区分でも直らない「作り直し」 は欄に
  // 帰着しないので、 モーダル上部の Alert に出す (design action-reason.md の区分 1)。
  const reselectFieldBlock =
    reselectBlock === 'deleteAndRecreate' ? undefined : reselectBlock
  const areaSelectOptions: DsSelectOption[] = areaSelectOptionsFor(
    selectedIssuer,
    watchedAreaCode,
  ).map(({ area, label, disabled }) => ({
    value: String(area),
    label,
    disabled,
  }))
  const lockedAreaLabel =
    watchedAreaCode === undefined
      ? ''
      : (areaSelectOptions.find(
          ({ value }) => value === String(watchedAreaCode),
        )?.label ?? areaLabelOf(watchedAreaCode))
  const handleSave = (): void => {
    // 二重防御 (footer の isSaveDisabled と同一条件)。 決定 2-B: 設定が
    // 読めるまで登録を確定させない。
    if (isFeeAutoConsiderBlocking) return
    issuerForm.submit(
      (data) => {
        // AC-BSP-103: 上限数量(1札) 超過は保存をブロックする (= BidLimitHint は
        // 事前チェックの表示のみで submit を止めないため、 ここで別途弾く)。
        // クランプは行わない (bid-limits.md §1-2 参照)。 自動起票の札を数量を変えずに
        // 直す編集は手動起票と同じ扱いにならないので当てない (保存時の判定は backend)。
        const sentOrigin = originFor(data.volumeMw)
        const exceededQtyMax = appliesPerBidVolumeLimit(sentOrigin)
          ? perBidVolumeLimitExceeded(
              bidLimits,
              data.areaCode,
              data.direction,
              data.volumeMw,
            )
          : undefined
        if (exceededQtyMax !== undefined) {
          setError('volumeMw', {
            message: t('page:biddingSpot.form.volumeExceedsPerBidLimit', {
              max: exceededQtyMax,
              value: data.volumeMw,
            }),
          })
          return
        }
        // 排出係数紐付け ON なら判別コメント完成形を組み立て、 JEPX自由記述欄への
        // 直接入力 (issue #6154) を後ろに連結して memo_jepx へ格納する
        // (AC-BSP-086 / spec §7 機能詳細 4)。 OFF なら自由記述のみ。
        // 会社情報の取得失敗中は判別コメントを認識できず `parseEmissionComment` が
        // memo_jepx 全体を自由記述として復元しているので、 自由記述をそのまま連結すれば
        // 保存済みの値は無言で剥がれない。
        // 利用者が触っていなければ保存済みの文字列をそのまま送る ([[buildJepxNote]])。
        const linked = data.emissionLinked === true && companyInfo.registered
        const built = buildJepxNote({
          linkedRetailerId: linked ? companyInfo.retailerId : undefined,
          alpha: data.emissionAlpha ?? '',
          freeText: data.memoJepx ?? '',
          saved:
            initialBid !== undefined
              ? {
                  memoJepx: initialBid.memoJepx,
                  retailerId: companyInfo.retailerId,
                }
              : undefined,
        })
        // spec §7 機能詳細 4: 連結後も 70 文字以内に収める。 超えたら保存前にブロックし、
        // 自由記述を何文字まで短くすればよいかを示す。
        if (!built.ok) {
          setError('memoJepx', {
            message: t('page:biddingSpot.form.memoJepxCombinedLengthError', {
              max: built.freeTextMaxLength,
            }),
          })
          return
        }
        // 入札価格は backend が算出する (AC-BSP-004b / AC-BSP-106)。 プレビューで見ていた状態を付けて
        // 希望価格を送り (OFF でも付ける)、 OFF として扱う間は入力値を入札価格として送る。 編集では、
        // 編集した札だけをこの形で送り、 触っていない札は親が保存済みの入札価格のまま送る。
        const submission = priceEntries.resolveSubmission(t, [
          {
            key: 'price',
            price: data.priceYenPerKwh,
            isMarket: data.orderMethod === ORDER_METHOD_MARKET,
          },
        ])
        if (submission.kind === 'error') {
          setError('priceYenPerKwh', { message: submission.message })
          return
        }
        // hold の間は保存ボタンを押せない。
        if (submission.kind === 'hold') return
        // form の `memoJepx` は自由記述だけ、 onSubmit へ渡すのは連結後の note。
        onSubmit({
          ...data,
          feeAutoConsider: submission.confirmed.price,
          memoJepx: built.note !== '' ? built.note : undefined,
          origin: sentOrigin,
        })
      },
      (formErrors: FieldErrors<IndividualBidFormInput>) => {
        const alertMessage = resolveFormErrorAlertMessage(
          formErrors,
          t('page:biddingSpot.serverError.validation'),
        )
        if (alertMessage !== undefined) {
          setError('root.serverError', { message: alertMessage })
        } else {
          // 項目脇の表示だけに切り替わるので、 直前の失敗で出した alert を消す。
          clearErrors('root.serverError')
        }
      },
    )
  }

  return (
    <Modal
      footer={
        readOnly ? (
          // 表示専用は「閉じる」だけ。 保存できない画面に保存ボタンを残さない。
          <TertiaryButton onClick={handleClose}>
            {t('common:action.close')}
          </TertiaryButton>
        ) : (
          <SaveCancelFooter
            isPending={isSubmitting}
            isSaveDisabled={
              isFeeAutoConsiderBlocking || reselectBlock !== undefined
            }
            onCancel={handleClose}
            onSave={handleSave}
          />
        )
      }
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={
        readOnly
          ? t('page:biddingSpot.modal.individualViewTitle')
          : isEdit
            ? t('page:biddingSpot.modal.individualEditTitle')
            : t('page:biddingSpot.modal.individualCreateTitle')
      }
    >
      {/* 表示専用は native の `fieldset[disabled]` で中の入力をまとめて止める
          (= 個々の input に disabled を配らない。 メモ欄・checkbox まで確実に効く)。
          DS Select は native control ではないので `isDisabled` を個別に渡す。 */}
      <chakra.fieldset margin={0} padding={0} border="none" disabled={readOnly}>
        <VStack align="stretch" gap={4}>
          {/* 下書き以外の札を開いた時の案内 (spec §9 AC-BSP-066)。 なぜ編集できないか
              だけでなく、 **下書きへ戻す手順**まで出す (状態に依らず同一文言、 #3458)。
              条件は締切・ワークフローを含む `readOnly` ではなく「下書きへ戻せる状態か」
              — 案内した手順が実行できないと鉄則1 (案内と実挙動を一致させる) に反する
              ([[shouldShowNonDraftBidNotice]])。
              [design gap] `spot.html` の `#bid-new-modal` は表示専用時の理由表示を持たない。
              同 modal 内の警告と同じ Alert + 見出し / 本文の 2 段構成 ([[BidFormAlerts]])
              に合わせて描く。 */}
          {showsNonDraftNotice && (
            <Alert
              description={t('page:biddingSpot.nonDraftNotice.body')}
              status="info"
              title={t('page:biddingSpot.nonDraftNotice.title')}
            />
          )}
          {(errors.root?.serverError !== undefined ||
            errorMessage !== undefined) && (
            <Alert
              status="error"
              title={errorMessage ?? errors.root?.serverError?.message}
            />
          )}
          {reselectBlock === 'deleteAndRecreate' && (
            <Alert
              status="error"
              title={t(
                'page:biddingSpot.form.draftIssuerReselect.deleteAndRecreate',
              )}
            />
          )}
          {/* 「複製」(AC-BSP-011) で開いた新規作成モードだと分かるよう案内する。
              受渡日はフォームに項目が無い (親 page が締切前の受渡日へ切り替えた値を
              使う) ため、 複製元とは別の保存先の日付を明示する。 */}
          {source.mode === 'duplicate' && (
            <Alert
              description={t('page:biddingSpot.modal.duplicateNoticeBody', {
                date: formatDateDisplay(source.targetDate),
              })}
              status="info"
              title={t('page:biddingSpot.modal.duplicateNoticeTitle')}
            />
          )}
          {/* 逆転 / 制限違反の警告 + 次アクション (design `#bnew-row-alert`)。
            入力の手前に置く = 何を直せばよいかを先に示す。 */}
          <BidFormAlerts
            alerts={formAlerts}
            hideActions={readOnly}
            onFocusField={focusField}
          />
          {/* 発注主体区分。 全入札 UI 共通の [[IssuerSelector]] を使う
              (以前はここだけライセンス 4 値のハードコードだった。 issue #5366)。
              位置は**入力欄の一番上** — 一括作成 modal と同じで、 design
              `spot.html` も `#bnew-issuer-selector` を エリア / 売買 より前に置く
              (2026-08-24 デザイナー指摘)。 「誰の札か」 を決めてから中身を書く順。 */}
          {/* 既存の下書きを開いて編集する時は読み取り専用 (AC-BSP-001a ※)。 入札一覧は
              ツールバーの区分でスコープされるので、 編集で区分を変えて保存すると札が一覧から
              消える。 変えるなら削除して作り直す。 新規作成・複製・再入札は選べる。 札の区分が
              受渡日の選択肢に無い時 (区分が空欄の取り込んだ札等) だけは、 保存できるよう選び直させる。 */}
          <IssuerSelectorView
            errorMessage={
              errors.issuerType?.message ??
              (reselectFieldBlock !== undefined
                ? t(
                    `page:biddingSpot.form.draftIssuerReselect.${reselectFieldBlock}`,
                  )
                : undefined)
            }
            isDisabled={readOnly}
            isLocked={isEdit && isSavedIssuerFixed}
            onChange={issuerForm.onChange}
            options={issuerForm.options}
            value={watchedIssuerType}
          />
          <FieldRoot invalid={errors.direction !== undefined} required>
            <FieldLabel>
              {t('page:biddingSpot.form.side')}
              {!readOnly && <RequiredMark />}
            </FieldLabel>
            {/* 売/買 は select ではなく **radio** (design `spot.html` `#bnew-dir` は
                `role="radiogroup"` + `.ecl-trade-label--buy/--sell` の色付きラベル)。
                2 択しかなく、 かつ色で買/売を示す項目なので、 開いて選ぶ select より
                その場で両方が見える radio が合う (2026-08-24 デザイナー指摘)。 */}
            <Controller
              control={control}
              name="direction"
              render={({ field: f }) => (
                <TradeSideRadioGroup
                  ariaLabel={t('page:biddingSpot.form.side')}
                  isDisabled={readOnly}
                  name="individualBid-direction"
                  onChange={(next) => {
                    f.onChange(Number(next))
                  }}
                  value={f.value !== undefined ? String(f.value) : ''}
                  values={{
                    buy: String(Side.BUY),
                    sell: String(Side.SELL),
                  }}
                />
              )}
            />
            <FieldErrorText>{errors.direction?.message}</FieldErrorText>
          </FieldRoot>
          <FieldRoot invalid={errors.areaCode !== undefined} required>
            <FieldLabel>
              {t('page:biddingSpot.form.area')}
              {!isAreaLocked && <RequiredMark />}
            </FieldLabel>
            {isAreaLocked && !readOnly ? (
              <Input
                aria-label={t('page:biddingSpot.form.area')}
                readOnly
                value={lockedAreaLabel}
              />
            ) : (
              <FormSelect
                width="100%"
                aria-label={t('page:biddingSpot.form.area')}
                control={control}
                isDisabled={isAreaLocked}
                name="areaCode"
                numeric
                options={areaSelectOptions}
                placeholder={t('common:form.selectPlaceholder')}
              />
            )}
            <FieldErrorText>{errors.areaCode?.message}</FieldErrorText>
          </FieldRoot>
          {/* コマは番号入力ではなく「28 (13:30〜14:00)」の select で選ぶ
            (issue #5365 Des 裁定 2026-08-18: 時間前入札と同じ全画面共通表記。
            コマリボンから開く SpotBidCreateModal と同じ選択肢 [[SPOT_KOMA_OPTIONS]])。 */}
          <FieldRoot invalid={errors.timeCode !== undefined} required>
            <FieldLabel>
              {t('page:biddingSpot.form.timeSlot')}
              {!readOnly && <RequiredMark />}
            </FieldLabel>
            <FormSelect
              width="100%"
              aria-label={t('page:biddingSpot.form.timeSlot')}
              control={control}
              isDisabled={readOnly}
              name="timeCode"
              numeric
              options={SPOT_KOMA_OPTIONS}
            />
            <FieldErrorText>{errors.timeCode?.message}</FieldErrorText>
          </FieldRoot>
          {/* 価格種別 (指値 / 成行、 AC-BSP-004a)。 成行はエリア別の成行注文許可設定
              (`bid-limits.md` §1-1 が SoT) に従い選択肢をグレーアウトし、 選択時は
              価格入力欄を無効化する (design `spot.html` `#bnew-price-type`)。 */}
          <FieldRoot invalid={errors.orderMethod !== undefined} required>
            <FieldLabel>
              {t('page:biddingSpot.form.orderMethod')}
              {!readOnly && <RequiredMark />}
            </FieldLabel>
            <RadioGroupRoot
              disabled={readOnly}
              onValueChange={(details: { value: string | null }) => {
                setValue(
                  'orderMethod',
                  details.value === 'market'
                    ? BiddingSpotOrderMethod.MARKET
                    : BiddingSpotOrderMethod.LIMIT,
                  { shouldValidate: true },
                )
              }}
              value={isMarket ? 'market' : 'limit'}
            >
              <HStack gap={4}>
                <RadioGroupItem value="limit">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText>
                    {t('page:biddingSpot.orderMethod.limit')}
                  </RadioGroupItemText>
                </RadioGroupItem>
                <RadioGroupItem disabled={marketBlocked} value="market">
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText
                    color={marketBlocked ? 'Text.disabled' : undefined}
                  >
                    {t('page:biddingSpot.orderMethod.market')}
                    {watchedAreaCode !== undefined && marketBlocked
                      ? t('page:biddingSpot.orderMethod.marketDisabledSuffix')
                      : ''}
                  </RadioGroupItemText>
                </RadioGroupItem>
              </HStack>
            </RadioGroupRoot>
            {/* 成行不許可エリアの案内 (AC-BSP-004a 規定文言)。 設定画面への導線は
                `AC-BDL-001〜004` (bid-limits スポット成行注文許可カード)。 */}
            {watchedAreaCode !== undefined && marketBlocked && (
              <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
                {t('common:bidding.marketOrderHint.prefix', {
                  area: areaLabelOf(watchedAreaCode) ?? String(watchedAreaCode),
                })}
                {/* 作成ダイアログの中から別の管理画面へ移るリンクは新しいタブで開く
                    (UI 辞書 `components/link-target.md`「別途指定」 2 行目 = 同じタブだと入力中の
                    内容が確認なしに失われる。 2026-10-16 Des 判断。 8080 も target="_blank")。
                    内部遷移なので external icon は付けない。 */}
                <Link asChild>
                  <RouterLink
                    rel="noopener noreferrer"
                    target="_blank"
                    to={routePaths.adminBidLimits.path}
                  >
                    {t('common:bidding.marketOrderHint.link')}
                  </RouterLink>
                </Link>
                {t('common:bidding.marketOrderHint.suffix')}
              </Text>
            )}
            <FieldErrorText>{errors.orderMethod?.message}</FieldErrorText>
          </FieldRoot>
          <FieldRoot invalid={errors.priceYenPerKwh !== undefined} required>
            <FieldLabel htmlFor="individualBid-priceYenPerKwh">
              {t(
                priceFee.kind === 'on'
                  ? 'page:biddingSpot.form.desiredPriceYenPerKwh'
                  : 'page:biddingSpot.form.bidPrice',
              )}
              {!readOnly && !isMarket && <RequiredMark />}
            </FieldLabel>
            {/* `width="100%"`: 指定が無いと `<input type="number">` はブラウザ既定の
                内容幅 (約 180px) で描かれ、 placeholder「例：10.00」 が見切れる
                (2026-08-24 デザイナー指摘)。 同じ modal の select は既に
                `width="100%"` を持っており、 入力欄だけ幅が揃っていなかった。 */}
            <Input
              width="100%"
              disabled={isMarket || isPriceEntryPending}
              max={priceConstraint.max}
              min={priceConstraint.min}
              placeholder={t('page:biddingSpot.form.bidPricePlaceholder')}
              step={priceConstraint.step}
              type="number"
              {...priceReg}
              borderColor={
                formAlerts.isInversion && errors.priceYenPerKwh === undefined
                  ? 'Border.negativeMid'
                  : undefined
              }
              id="individualBid-priceYenPerKwh"
              // design は逆転時に価格欄へ `is-error` (赤枠) を付ける (`_setFieldErrorBelow`)。
              // ただし `aria-invalid` は立てない — 逆転は spec AC-BSP-009 で
              // 「ブロックしない」 = 保存できるので、 支援技術に検証エラーとして
              // 伝えるのは誤り。 見た目だけ design に合わせる。
              onChange={wrapNumberInputChange(priceReg, priceConstraint)}
            />
            <FieldErrorText>{errors.priceYenPerKwh?.message}</FieldErrorText>
            {/* design は価格欄の直下に「現在値 X 円/kWh — …逆転しています」 を出す。
              冒頭 alert が「何が起きたか」、 こちらが「今いくらで起きているか」。
              色は design の field error スロットと同じ赤 (2026-08-18 デザイナー提示の
              8080 スクリーンショット準拠)。 冒頭 alert が warning (黄) なのに対し
              こちらが赤なのは 8080 の実装どおり。 */}
            {/* 判定は入札価格 (alertPrice) で行うが、 「現在値」 は design どおり価格欄に入っている値
              (ON なら希望価格) を出す。 欄の値と食い違わせない。 */}
            {formAlerts.isInversion && hintPrice !== undefined && (
              <Text color="Text.negativeHigh" fontSize="sm">
                {t('page:biddingSpot.formAlert.inversion.priceHint', {
                  price: hintPrice,
                })}
              </Text>
            )}
            {/* 手数料自動考慮 (AC-BDL-016 / AC-BSP-004b) の入札価格プレビュー。 調整後の
                入札価格が JEPX 制度上限を外れる間はエラー文言に置き換える (AC-BSP-050)。
                表示専用で開いた札も design どおり OFF の案内・単価を取得できない案内を出す
                (design の補足表示は表示専用で分けない)。 成行は価格を入力しない
                (価格欄が無効) ので、 どの扱いでも案内を出さない (design の補足表示は成行で空。
                AC-BSP-004b は成行を対象外とする)。 */}
            {!isMarket && (!readOnly || !isPriceEntryPending) && (
              // 開いた札の逆算を待つ間は、 欄に残る保存済みの入札価格を希望価格と読ませないよう、
              // 入力待ちの案内ではなく確認中の案内を出す。
              <FeeAutoConsiderHint
                fee={priceFee}
                isSettingsUnavailable={isSettingsUnavailable}
                preview={feePreview}
              />
            )}
          </FieldRoot>
          <FieldRoot invalid={errors.volumeMw !== undefined} required>
            <FieldLabel htmlFor="individualBid-volumeMw">
              {t('page:biddingSpot.form.bidVolume')}
              {!readOnly && <RequiredMark />}
            </FieldLabel>
            {/* `width="100%"`: 指定が無いと `<input type="number">` はブラウザ既定の
                内容幅 (約 180px) で描かれ、 placeholder「例：10.00」 が見切れる
                (2026-08-24 デザイナー指摘)。 同じ modal の select は既に
                `width="100%"` を持っており、 入力欄だけ幅が揃っていなかった。 */}
            <Input
              width="100%"
              max={JEPX_VOLUME_CONSTRAINT.max}
              min={JEPX_VOLUME_CONSTRAINT.min}
              placeholder={t('page:biddingSpot.form.bidVolumePlaceholder')}
              step={JEPX_VOLUME_CONSTRAINT.step}
              type="number"
              {...volumeReg}
              id="individualBid-volumeMw"
              onChange={wrapNumberInputChange(
                volumeReg,
                JEPX_VOLUME_CONSTRAINT,
              )}
            />
            <FieldErrorText>{errors.volumeMw?.message}</FieldErrorText>
            {/* 入札制限インラインヒント (design (B))。エリア + 売/買 確定後に表示。
              数量欄の直下に配置し、spacing は design の margin-top:4px = space-1 に合わせる。
              hint には価格・数量の両制限を併記する (= BidLimitHint の統合ビュー)。 */}
            {watchedAreaCode !== undefined &&
              watchedDirection !== undefined && (
                <Box marginTop={1}>
                  <BidLimitHint
                    checksVolumeMax={appliesPerBidVolumeLimit(
                      originFor(watchedVolume),
                    )}
                    limit={resolvedLimit}
                    price={alertPrice}
                    volumeMw={hintVolume}
                  />
                </Box>
              )}
          </FieldRoot>
          <MemoFields
            emission={{
              registered: companyInfo.registered,
              retailerId: companyInfo.retailerId,
              fetchFailed: companyInfo.isError,
              isLoading: companyInfo.isLoading,
              linked: watch('emissionLinked') ?? false,
              alpha: watch('emissionAlpha') ?? '',
              alphaError: errors.emissionAlpha?.message,
              onLinkedChange: (linked) => {
                setValue('emissionLinked', linked)
              },
              onAlphaChange: (alpha) => {
                setValue('emissionAlpha', alpha, { shouldValidate: true })
              },
            }}
            freeError={errors.memoFree?.message}
            freeValue={watch('memoFree') ?? ''}
            idPrefix="individualBid"
            jepxFree={{
              value: watch('memoJepx') ?? '',
              register: register('memoJepx'),
              error: errors.memoJepx?.message,
              maxLength: jepxFreeMaxLength,
              // 会社情報の到着で復元をやり直すので、 届くまでは入力させない。
              disabled: companyInfo.isLoading,
            }}
            registerFree={register('memoFree')}
          />
        </VStack>
      </chakra.fieldset>
    </Modal>
  )
}
